import * as React from 'react';
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {map, merge, mapValues, keyBy, isEqual, isNull} from "lodash";
import useAfterUpdateEffect from "../utils/useAfterUpdateEffect";
import useDidMountEffect from "../utils/useDidMountEffect";
import Icon from "./Icon";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import Button from "../components/Button";
import {useState} from "react";
import {toast} from "react-toastify";
import {debounceTime, distinctUntilChanged, Subject} from "rxjs";
import {withTranslation} from "react-i18next";

const ModalDelete = (props) => {
  const [deleting, setDeleting] = useState(false)

  return <Modal isOpen className="modal-danger">
    <ModalHeader>Potwierdź usunięcie</ModalHeader>
    <ModalBody>
      Czy na pewno chcesz usunąć wskazaną pozycję?
    </ModalBody>
    <ModalFooter>
      <Button color={'secondary'} onClick={props.onClose}>Anuluj</Button>
      <Button color="danger" loading={deleting} onClick={() => {
        setDeleting(true)

        props.deleteFn(props.id)
          .then(() => {
            props.onDeleted()
          })
          .finally(() => {
            props.onClose()
            setDeleting(false)
          })
      }}>
        <i className="fas fa-trash" />
        <span>Tak, usuń</span>
      </Button>
    </ModalFooter>
  </Modal>
}

function AppSelect(props) {
  const selectRef = React.createRef();
  const [fetchOptions$, setFetchOptions] = React.useState(new Subject());
  const [value, setValue] = React.useState(props.value);
  const [q, setQ] = React.useState('');
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [defaultOptions, setDefaultOptions] = React.useState(props.options);
  const [filters, setFilters] = React.useState(props.filters);
  const [deleteOnModal, setDeleteOnModal] = useState()

  useDidMountEffect(() => {
    fetchOptions$.pipe(
      debounceTime(props.debounceTime),
      distinctUntilChanged(),
    ).subscribe(({queryParams, callback}) => {
      props.apiFn(map(queryParams, (value, key) => [key, value].join('=')).join('&'))
        .then(res => {
          setData(res.data['hydra:member']);
          callback(parseDataToOptions(res.data['hydra:member']));
        })
        .catch(res => {

        })
    })

    if(props.menuIsOpen){
      selectRef.current.focus();
    }
  })

  useAfterUpdateEffect(() => {
    setValue(props.value)
  }, [props.value])

  useAfterUpdateEffect(() => {
    setDefaultOptions(props.options)
  }, [props.options])

  useAfterUpdateEffect(() => {
    setData([])
    setDefaultOptions([])
    fetchDefaultOptions();
  }, [filters])

  useAfterUpdateEffect(() => {
    if(!isEqual(props.filters, filters)){
      setFilters(props.filters)
    }
  }, [props.filters])

  React.useEffect(() => {
    fetchDefaultOptions();
  }, []);

  const fetchDefaultOptions = () => {
    if(!props.apiFn){
      return
    }

    setLoading(true);

    let queryParams = {
      page: 1,
      itemsPerPage: props.fetchItemsCount,
      ...getFetchOrder(),
      ...filters,
    };

    props.apiFn(map(queryParams, (value, key) => [key, value].join('=')).join('&'))
      .then(res => {
        setDefaultOptions(res.data['hydra:member']);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      })
  }

  const valueToOption = (value) => ({
      value: value.id,
      label: props.getLabelSelectedFn ? props.getLabelSelectedFn(value) : props.getLabelFn(value),
    }
  )
  const getFetchOrder = () => {
    return mapValues(keyBy(map(props.fetchOrder, (value, key) => (
      {
        key: `order[${key}]`,
        value,
      }
    )), 'key'), 'value')
  }

  const fetchOptions = (search, callback) => {
    if(!props.apiFn){
      return
    }

    let queryParams = {
      q,
      page: 1,
      itemsPerPage: props.fetchItemsCount,
      ...getFetchOrder(),
      ...filters,
    };

    fetchOptions$.next({queryParams, callback})
  }

  const handleInputChange = (newValue) => {
    // const q = newValue.replace(/\W/g, '');
    setQ(newValue);
    return newValue;
  }

  let Component
  let componentProps = {}
  if(props.apiFn){
    Component = props.isCreatable ? AsyncCreatableSelect : AsyncSelect
    componentProps = {
      createOptionPosition: 'first',
      defaultOptions: defaultOptions.map(data => ({
          value: data.id,
          label: props.getLabelFn(data),
        }
      ))
    }
  }else{
    Component = props.isCreatable ? CreatableSelect : Select
    componentProps = {
      options: defaultOptions.map(data => ({
          value: data.id,
          label: props.apiDeleteFn ? <div className="d-flex justify-content-between align-items-center hoverable">
            {props.getLabelFn(data)}
            <Icon className="fas fa-trash cursor-pointer text-secondary hover-display" tooltip={"Usuń pozycję"} onClick={e => {
              setDeleteOnModal(data.id)
              e.stopPropagation()
            }} />
          </div> : props.getLabelFn(data),
        }
      ))
    }
  }

  const parseDataToOptions = (data) => {
    if(props.parseDataToOptions){
      return props.parseDataToOptions(data, props)
    }

    return data.map(data => ({
        value: data.id,
        label: props.getLabelFn(data),
      }
    ))
  }

  return <React.Fragment>
    <Component
      {...componentProps}
      ref={selectRef}
      classNamePrefix="react-select"
      formatGroupLabel={props.formatGroupLabel}
      menuIsOpen={props.menuIsOpen}
      menuPortalTarget={props.menuPortalTarget}
      styles={props.styles}
      menuPlacement={props.menuPlacement}
      isLoading={loading}
      isMulti={props.isMulti}
      closeMenuOnSelect={!props.isMulti}
      isSearchable={props.isSearchable}
      isClearable={props.isClearable}
      formatCreateLabel={props.formatCreateLabel}
      className={props.className}
      defaultOptions={parseDataToOptions(defaultOptions)}
      noOptionsMessage={props.noOptionsMessage}
      loadingMessage={() => <span>{props.t('common:search_in_progress')}...</span>}
      placeholder={props.placeholder}
      defaultInputValue={props.defaultInputValue}
      loadOptions={fetchOptions}
      onInputChange={handleInputChange}
      value={value ? (props.isMulti ? value.map(v => valueToOption(v)) : valueToOption(value)) : null}
      onChange={option => {
        if(isNull(option)){
          setValue(null)
          props.onChange(null);
        // }else if(isNull(option.value)) {
        //   setValue(null)
        }else if(option?.__isNew__){
          if(props.apiCreateFn){
            props.apiCreateFn({
              name: option.value
            }).then(res => {
              props.onChange(res.data)
              setValue(res.data)
            })
          }else{
            props.onChange(option.value);
            setValue({
                value: option.value,
                label: option.value,
              }
            )
          }
        }else{
          let val = null;
          if(option){
            const options = merge(defaultOptions, data)

            if(props.isMulti){
              val = option.map(v => options.find(opt => opt.id === v.value));
            }else{
              val = options.find(opt => opt.id === option.value);
            }
          }
          props.onChange(val);
          setValue(val);
        }
      }}
      onBlur={props.onBlur}
    />
    {!!deleteOnModal && <ModalDelete
      id={deleteOnModal}
      deleteFn={props.apiDeleteFn}
      onDeleted={() => {
        toast.success('Pozycja została usunięta')
        setDeleteOnModal(null)
      }}
      onClose={() => {
        setDeleteOnModal(null)
      }}
    />}
  </React.Fragment>;
}

AppSelect.defaultProps = {
  debounceTime: 0,
  options: [],
  noOptionsMessage: () => <span>Brak</span>,
  formatCreateLabel: name => <span><i className="fas fa-plus" /> Utwórz: {name}</span>,
  isMulti: false,
  isSearchable: true,
  isClearable: true,
  isCreatable: false,
  menuPlacement: 'bottom',
  className: '',
  placeholder: 'Wybierz...',
  getLabelFn: (data) => data.name,
  fetchItemsCount: 30,
  fetchOrder: {
    'id': 'desc',
  },
  filters: {},
};

AppSelect.propTypes = {
  debounceTime: PropTypes.number,
  noOptionsMessage: PropTypes.func,
  menuPlacement: PropTypes.string,
  filters: PropTypes.object,
  apiFn: PropTypes.func,
  apiCreateFn: PropTypes.func,
  options: PropTypes.array,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isCreatable: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  getLabelFn: PropTypes.func,
  getLabelSelectedFn: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  fetchItemsCount: PropTypes.number,
  defaultInputValue: PropTypes.string,
  formatGroupLabel: PropTypes.func,
  value: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string,]).isRequired,
  }),
  fetchOrder: PropTypes.object,
  menuIsOpen: PropTypes.bool,
};

export default withTranslation()(AppSelect);
